import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Space, Tabs, Progress,
  Typography, Badge, Modal, Form, Input, Select, InputNumber, message,
  Tooltip, Drawer, Descriptions, Timeline, Empty, Spin, Popconfirm,
  Divider, Switch, Rate, Dropdown, Alert,
} from 'antd';
import {
  HomeOutlined, TeamOutlined, DollarOutlined, RocketOutlined,
  RobotOutlined, EyeOutlined, PlusOutlined, ReloadOutlined,
  ArrowUpOutlined, ArrowDownOutlined, PhoneOutlined, MailOutlined,
  EnvironmentOutlined, HeartOutlined, StarOutlined, ThunderboltOutlined,
  CheckCircleOutlined, ClockCircleOutlined, FireOutlined, TrophyOutlined,
  MessageOutlined, VideoCameraOutlined, FileTextOutlined, GiftOutlined,
  UserOutlined, GlobalOutlined, SearchOutlined, BulbOutlined,
  SafetyCertificateOutlined, ImportOutlined, TranslationOutlined,
  FacebookOutlined, SendOutlined, GoogleOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const T = {
  en: {
    platformTitle: 'HOUSE AI Platform',
    platformSubtitle: 'AI-Powered Real Estate Management — Life Property Bangkok',
    importDemo: 'Import Demo Data',
    addProperty: 'Add Property',
    addLead: 'Add Lead',
    refresh: 'Refresh',
    crawlerOn: 'External Crawler: ON',
    crawlerOff: 'External Crawler: OFF',
    crawlerOnDesc: 'Importing photos & listings from external sites is allowed',
    crawlerOffDesc: 'Importing photos & listings from external sites is disabled',
    crawlerError: 'Failed to update crawler switch',
    generatingDemo: 'Generating 20 demo listings...',
    demoImported: 'Demo properties imported!',
    propertyCreated: 'Property created',
    leadCreated: 'Lead created',
    aiEnhancing: 'AI enhancing...',
    aiEnhanced: 'AI enhanced!',
    aiResponding: 'AI responding...',
    aiResponseSent: 'AI response sent',
    milestoneExecuted: 'Milestone executed',
    vsLastMonth: 'vs last month',
    // Stat cards
    properties: 'Properties',
    totalLeads: 'Total Leads',
    activeLeads: 'Active Leads',
    dealsMonth: 'Deals (Month)',
    revenueMonth: 'Revenue (Month)',
    aiResponsesToday: 'AI Responses Today',
    conversionRate: 'Conversion Rate',
    portalViews: 'Portal Views',
    // Tabs
    tabDashboard: 'Dashboard',
    tabProperties: 'Properties',
    tabAdPosts: 'Ad Posts',
    tabLeads: 'Leads',
    tabNegotiations: 'Negotiations',
    tabContracts: 'Contracts',
    tabTours: 'Virtual Tours',
    tabAftercare: 'Aftercare',
    tabReferrals: 'Referrals',
    adPostChannel: 'Channel',
    adPostPublished: 'Published',
    adPostLeads: 'Leads',
    adPostLink: 'Link',
    adViewLeads: 'View Leads',
    adNoLeads: 'No leads yet',
    // Dashboard cards
    leadPipeline: 'Lead Pipeline',
    aiActivityFeed: 'AI Activity Feed',
    topProperties: 'Top Properties',
    noAiActivity: 'No AI activity yet',
    // Property table
    colProperty: 'Property',
    colType: 'Type',
    colListing: 'Listing',
    colPrice: 'Price',
    colBeds: 'Beds',
    colArea: 'Area',
    colStats: 'Stats',
    colStatus: 'Status',
    colActions: 'Actions',
    tipAiEnhance: 'AI Enhance',
    tipViewPortal: 'View Portal',
    tipFbPost: 'Post to Facebook',
    tipGads: 'Create Google Ads Campaign',
    gadsCreating: 'Creating Google Ads search campaign...',
    gadsCreated: 'Google Ads Campaign Created!',
    gadsCreatedDesc: 'has created a Google Ads search campaign.',
    gadsStatus: 'Status',
    gadsReviewNote: '(review and enable it in the Google Ads dashboard)',
    gadsManage: 'Go to Google Ads',
    gadsFailed: 'Google Ads campaign creation failed',
    tipViews: 'Views',
    tipInquiries: 'Inquiries',
    // Lead table
    colLead: 'Lead',
    colScore: 'Score',
    colStage: 'Stage',
    colSource: 'Source',
    colSourceSite: 'Source Site',
    allSources: 'All Sources',
    colBudget: 'Budget',
    colNationality: 'Nationality',
    tipAiRespond: 'AI Respond',
    tipDetails: 'Details',
    // Negotiation table
    colId: 'ID',
    colPropertyId: 'Property',
    colLeadId: 'Lead',
    colAsking: 'Asking',
    colOffer: 'Offer',
    colFinal: 'Final',
    // Contract table
    colContractType: 'Type',
    colCreated: 'Created',
    // Tour table
    colTourType: 'Type',
    colTourViews: 'Views',
    colAiGenerated: 'AI Generated',
    aiTag: 'AI',
    manualTag: 'Manual',
    // Aftercare table
    colPlanType: 'Type',
    executeNext: 'Execute Next',
    // Referral table
    colReferrer: 'Referrer',
    colReferred: 'Referred',
    colEmail: 'Email',
    colReward: 'Reward',
    paid: 'Paid',
    pending: 'Pending',
    generateTours: 'Generate Tours',
    generatingTours: 'Generating tours for all properties...',
    toursGenerated: 'tours created',
    viewTour: 'View Tour',
    // Property modal
    modalAddProperty: 'Add Property',
    create: 'Create',
    labelTitle: 'Title',
    labelPartnerSlug: 'Partner Slug',
    labelType: 'Type',
    labelListing: 'Listing',
    labelPrice: 'Price',
    labelBedrooms: 'Bedrooms',
    labelBathrooms: 'Bathrooms',
    labelArea: 'Area (sqm)',
    labelDistrict: 'District',
    labelBuilding: 'Building',
    labelFurnishing: 'Furnishing',
    labelDescription: 'Description',
    typeCondo: 'Condo',
    typeHouse: 'House',
    typeVilla: 'Villa',
    typeTownhouse: 'Townhouse',
    typeLand: 'Land',
    typeCommercial: 'Commercial',
    listRent: 'Rent',
    listSale: 'Sale',
    listBoth: 'Both',
    furnFully: 'Fully',
    furnPartial: 'Partial',
    furnUnfurnished: 'Unfurnished',
    // Lead modal
    modalAddLead: 'Add Lead',
    labelName: 'Name',
    labelEmail: 'Email',
    labelPhone: 'Phone',
    labelNationality: 'Nationality',
    labelSource: 'Source',
    labelBudgetMin: 'Budget Min',
    labelBudgetMax: 'Budget Max',
    labelNotes: 'Notes',
    // Lead drawer
    leadDetails: 'Lead Details',
    aiRespond: 'AI Respond',
    labelLanguage: 'Language',
    labelStage: 'Stage',
    labelScore: 'Score',
    notes: 'Notes',
    aiRecommendedProperties: 'AI Recommended Properties',
    noRecommendations: 'No recommendations',
    activityTimeline: 'Activity Timeline',
    noActivities: 'No activities',
    // Pipeline stages
    stageNew: 'New',
    stageContacted: 'Contacted',
    stageNurturing: 'Nurturing',
    stageHot: 'Hot',
    stageViewing: 'Viewing',
    stageNegotiating: 'Negotiating',
    stageWon: 'Won',
    stageLost: 'Lost',
    stagePostSale: 'Post-Sale',
    stageReferral: 'Referral',
  },
  th: {
    platformTitle: 'HOUSE AI แพลตฟอร์ม',
    platformSubtitle: 'การจัดการอสังหาริมทรัพย์ด้วย AI — Life Property กรุงเทพฯ',
    importDemo: 'นำเข้าข้อมูลตัวอย่าง',
    addProperty: 'เพิ่มอสังหาฯ',
    addLead: 'เพิ่มลูกค้า',
    refresh: 'รีเฟรช',
    crawlerOn: 'External Crawler: ON',
    crawlerOff: 'External Crawler: OFF',
    crawlerOnDesc: 'อนุญาตให้ดึงรูปและข้อมูลจากเว็บภายนอกได้',
    crawlerOffDesc: 'ปิดการดึงรูปและข้อมูลจากเว็บภายนอก',
    crawlerError: 'ไม่สามารถอัปเดตสวิตช์ครอว์เลอร์ได้',
    generatingDemo: 'กำลังสร้างรายการตัวอย่าง 20 รายการ...',
    demoImported: 'นำเข้าอสังหาฯ ตัวอย่างแล้ว!',
    propertyCreated: 'สร้างอสังหาฯ แล้ว',
    leadCreated: 'สร้างลูกค้าแล้ว',
    aiEnhancing: 'AI กำลังปรับปรุง...',
    aiEnhanced: 'AI ปรับปรุงแล้ว!',
    aiResponding: 'AI กำลังตอบ...',
    aiResponseSent: 'AI ตอบกลับแล้ว',
    milestoneExecuted: 'ดำเนินการเรียบร้อย',
    vsLastMonth: 'เทียบเดือนที่แล้ว',
    properties: 'อสังหาริมทรัพย์',
    totalLeads: 'ลูกค้าทั้งหมด',
    activeLeads: 'ลูกค้าที่สนใจ',
    dealsMonth: 'ดีลประจำเดือน',
    revenueMonth: 'รายได้ประจำเดือน',
    aiResponsesToday: 'AI ตอบวันนี้',
    conversionRate: 'อัตราการปิดดีล',
    portalViews: 'ยอดเข้าชม',
    tabDashboard: 'แดชบอร์ด',
    tabProperties: 'อสังหาริมทรัพย์',
    tabAdPosts: 'โพสต์',
    tabLeads: 'ลูกค้า',
    tabNegotiations: 'การเจรจา',
    tabContracts: 'สัญญา',
    tabTours: 'ทัวร์เสมือนจริง',
    tabAftercare: 'บริการหลังการขาย',
    tabReferrals: 'การแนะนำ',
    adPostChannel: 'ช่องทาง',
    adPostPublished: 'เผยแพร่',
    adPostLeads: 'ลีด',
    adPostLink: 'ลิงก์',
    adViewLeads: 'ดูลีด',
    adNoLeads: 'ยังไม่มีลีด',
    leadPipeline: 'ไปป์ไลน์ลูกค้า',
    aiActivityFeed: 'กิจกรรม AI',
    topProperties: 'อสังหาฯ ยอดนิยม',
    noAiActivity: 'ยังไม่มีกิจกรรม AI',
    colProperty: 'อสังหาริมทรัพย์',
    colType: 'ประเภท',
    colListing: 'ประกาศ',
    colPrice: 'ราคา',
    colBeds: 'ห้องนอน',
    colArea: 'พื้นที่',
    colStats: 'สถิติ',
    colStatus: 'สถานะ',
    colActions: 'การดำเนินการ',
    tipAiEnhance: 'AI ปรับปรุง',
    tipViewPortal: 'ดูพอร์ทัล',
    tipFbPost: 'โพสต์ไปยัง Facebook',
    tipGads: 'สร้างแคมเปญ Google Ads',
    gadsCreating: 'กำลังสร้างแคมเปญ Google Ads...',
    gadsCreated: 'สร้างแคมเปญ Google Ads สำเร็จ!',
    gadsCreatedDesc: 'ได้สร้างแคมเปญ Google Ads เรียบร้อยแล้ว',
    gadsStatus: 'สถานะ',
    gadsReviewNote: '(ตรวจสอบและเปิดใช้งานในแดชบอร์ด Google Ads)',
    gadsManage: 'ไปยัง Google Ads',
    gadsFailed: 'การสร้างแคมเปญ Google Ads ล้มเหลว',
    tipViews: 'ยอดชม',
    tipInquiries: 'สอบถาม',
    colLead: 'ลูกค้า',
    colScore: 'คะแนน',
    colStage: 'ขั้นตอน',
    colSource: 'แหล่งที่มา',
    colSourceSite: 'แหล่งข้อมูล',
    allSources: 'ทุกแหล่ง',
    colBudget: 'งบประมาณ',
    colNationality: 'สัญชาติ',
    tipAiRespond: 'AI ตอบกลับ',
    tipDetails: 'รายละเอียด',
    colId: 'ID',
    colPropertyId: 'อสังหาฯ',
    colLeadId: 'ลูกค้า',
    colAsking: 'ราคาขอ',
    colOffer: 'ราคาเสนอ',
    colFinal: 'ราคาสุดท้าย',
    colContractType: 'ประเภท',
    colCreated: 'วันที่สร้าง',
    colTourType: 'ประเภท',
    colTourViews: 'ยอดชม',
    colAiGenerated: 'สร้างโดย AI',
    aiTag: 'AI',
    manualTag: 'ด้วยตนเอง',
    colPlanType: 'ประเภท',
    executeNext: 'ดำเนินการถัดไป',
    colReferrer: 'ผู้แนะนำ',
    colReferred: 'ผู้ถูกแนะนำ',
    colEmail: 'อีเมล',
    colReward: 'รางวัล',
    paid: 'จ่ายแล้ว',
    pending: 'รอดำเนินการ',
    generateTours: 'สร้างทัวร์',
    generatingTours: 'กำลังสร้างทัวร์สำหรับอสังหาฯ ทั้งหมด...',
    toursGenerated: 'ทัวร์ที่สร้างแล้ว',
    viewTour: 'ดูทัวร์',
    modalAddProperty: 'เพิ่มอสังหาริมทรัพย์',
    create: 'สร้าง',
    labelTitle: 'ชื่อ',
    labelPartnerSlug: 'รหัสพาร์ทเนอร์',
    labelType: 'ประเภท',
    labelListing: 'ประกาศ',
    labelPrice: 'ราคา',
    labelBedrooms: 'ห้องนอน',
    labelBathrooms: 'ห้องน้ำ',
    labelArea: 'พื้นที่ (ตร.ม.)',
    labelDistrict: 'เขต',
    labelBuilding: 'อาคาร',
    labelFurnishing: 'เฟอร์นิเจอร์',
    labelDescription: 'รายละเอียด',
    typeCondo: 'คอนโด',
    typeHouse: 'บ้าน',
    typeVilla: 'วิลล่า',
    typeTownhouse: 'ทาวน์เฮาส์',
    typeLand: 'ที่ดิน',
    typeCommercial: 'พาณิชย์',
    listRent: 'เช่า',
    listSale: 'ขาย',
    listBoth: 'ทั้งสอง',
    furnFully: 'ครบ',
    furnPartial: 'บางส่วน',
    furnUnfurnished: 'ไม่มี',
    modalAddLead: 'เพิ่มลูกค้า',
    labelName: 'ชื่อ',
    labelEmail: 'อีเมล',
    labelPhone: 'โทรศัพท์',
    labelNationality: 'สัญชาติ',
    labelSource: 'แหล่งที่มา',
    labelBudgetMin: 'งบขั้นต่ำ',
    labelBudgetMax: 'งบสูงสุด',
    labelNotes: 'หมายเหตุ',
    leadDetails: 'รายละเอียดลูกค้า',
    aiRespond: 'AI ตอบกลับ',
    labelLanguage: 'ภาษา',
    labelStage: 'ขั้นตอน',
    labelScore: 'คะแนน',
    notes: 'หมายเหตุ',
    aiRecommendedProperties: 'อสังหาฯ ที่ AI แนะนำ',
    noRecommendations: 'ไม่มีคำแนะนำ',
    activityTimeline: 'ไทม์ไลน์กิจกรรม',
    noActivities: 'ไม่มีกิจกรรม',
    stageNew: 'ใหม่',
    stageContacted: 'ติดต่อแล้ว',
    stageNurturing: 'ดูแล',
    stageHot: 'สนใจมาก',
    stageViewing: 'เข้าชม',
    stageNegotiating: 'เจรจา',
    stageWon: 'ปิดดีล',
    stageLost: 'เสียลูกค้า',
    stagePostSale: 'หลังการขาย',
    stageReferral: 'แนะนำ',
  },
  zh: {
    platformTitle: 'HOUSE AI 平台',
    platformSubtitle: 'AI 驅動不動產管理 — Life Property 曼谷',
    importDemo: '匯入展示資料',
    addProperty: '新增物件',
    addLead: '新增客戶',
    refresh: '重新整理',
    crawlerOn: '外部爬蟲：開啟',
    crawlerOff: '外部爬蟲：關閉',
    crawlerOnDesc: '允許從外部網站抓取圖片與物件資料',
    crawlerOffDesc: '已停用從外部網站抓取圖片與物件資料',
    crawlerError: '更新爬蟲開關失敗',
    generatingDemo: '正在建立 20 筆展示物件...',
    demoImported: '展示物件已匯入！',
    propertyCreated: '物件已建立',
    leadCreated: '客戶已建立',
    aiEnhancing: 'AI 增強中...',
    aiEnhanced: 'AI 增強完成！',
    aiResponding: 'AI 回覆中...',
    aiResponseSent: 'AI 已回覆',
    milestoneExecuted: '里程碑已執行',
    vsLastMonth: '較上月',
    properties: '物件',
    totalLeads: '總客戶數',
    activeLeads: '活躍客戶',
    dealsMonth: '本月成交',
    revenueMonth: '本月營收',
    aiResponsesToday: '今日 AI 回覆',
    conversionRate: '轉換率',
    portalViews: '入口瀏覽量',
    tabDashboard: '儀表板',
    tabProperties: '物件管理',
    tabAdPosts: '貼文成效',
    tabLeads: '客戶管理',
    tabNegotiations: '議價管理',
    tabContracts: '合約管理',
    tabTours: '虛擬導覽',
    tabAftercare: '售後服務',
    tabReferrals: '轉介管理',
    adPostChannel: '管道',
    adPostPublished: '發布時間',
    adPostLeads: '詢問數',
    adPostLink: '連結',
    adViewLeads: '查看詢問',
    adNoLeads: '尚無詢問',
    leadPipeline: '客戶流程管線',
    aiActivityFeed: 'AI 活動紀錄',
    topProperties: '熱門物件',
    noAiActivity: '尚無 AI 活動',
    colProperty: '物件',
    colType: '類型',
    colListing: '交易方式',
    colPrice: '價格',
    colBeds: '房間',
    colArea: '面積',
    colStats: '統計',
    colStatus: '狀態',
    colActions: '操作',
    tipAiEnhance: 'AI 增強',
    tipViewPortal: '檢視入口',
    tipFbPost: '發布到 Facebook',
    tipGads: '建立 Google Ads 廣告',
    gadsCreating: '正在建立 Google Ads 搜尋廣告活動...',
    gadsCreated: 'Google Ads 廣告活動建立成功！',
    gadsCreatedDesc: '已建立 Google Ads 搜尋廣告活動。',
    gadsStatus: '狀態',
    gadsReviewNote: '（需至 Google Ads 後台審核後啟用）',
    gadsManage: '前往 Google Ads',
    gadsFailed: 'Google Ads 廣告活動建立失敗',
    tipViews: '瀏覽次數',
    tipInquiries: '詢問次數',
    colLead: '客戶',
    colScore: '評分',
    colStage: '階段',
    colSource: '來源',
    colSourceSite: '來源網站',
    allSources: '全部來源',
    colBudget: '預算',
    colNationality: '國籍',
    tipAiRespond: 'AI 回覆',
    tipDetails: '詳情',
    colId: 'ID',
    colPropertyId: '物件',
    colLeadId: '客戶',
    colAsking: '要價',
    colOffer: '出價',
    colFinal: '成交價',
    colContractType: '類型',
    colCreated: '建立日期',
    colTourType: '類型',
    colTourViews: '瀏覽數',
    colAiGenerated: 'AI 生成',
    aiTag: 'AI',
    manualTag: '手動',
    colPlanType: '類型',
    executeNext: '執行下一步',
    colReferrer: '推薦人',
    colReferred: '被推薦人',
    colEmail: '電子郵件',
    colReward: '獎勵',
    paid: '已付',
    pending: '待付',
    generateTours: '產生導覽',
    generatingTours: '正在為所有物件產生虛擬導覽...',
    toursGenerated: '已建立導覽',
    viewTour: '查看導覽',
    modalAddProperty: '新增物件',
    create: '建立',
    labelTitle: '標題',
    labelPartnerSlug: '合作夥伴代碼',
    labelType: '類型',
    labelListing: '交易方式',
    labelPrice: '價格',
    labelBedrooms: '臥室',
    labelBathrooms: '衛浴',
    labelArea: '面積（平方公尺）',
    labelDistrict: '地區',
    labelBuilding: '大樓',
    labelFurnishing: '家具配置',
    labelDescription: '描述',
    typeCondo: '公寓',
    typeHouse: '透天',
    typeVilla: '別墅',
    typeTownhouse: '聯排別墅',
    typeLand: '土地',
    typeCommercial: '商業',
    listRent: '出租',
    listSale: '出售',
    listBoth: '租售皆可',
    furnFully: '全配',
    furnPartial: '部分',
    furnUnfurnished: '空屋',
    modalAddLead: '新增客戶',
    labelName: '姓名',
    labelEmail: '電子郵件',
    labelPhone: '電話',
    labelNationality: '國籍',
    labelSource: '來源',
    labelBudgetMin: '最低預算',
    labelBudgetMax: '最高預算',
    labelNotes: '備註',
    leadDetails: '客戶詳情',
    aiRespond: 'AI 回覆',
    labelLanguage: '語言',
    labelStage: '階段',
    labelScore: '評分',
    notes: '備註',
    aiRecommendedProperties: 'AI 推薦物件',
    noRecommendations: '暫無推薦',
    activityTimeline: '活動時間軸',
    noActivities: '暫無活動',
    stageNew: '新客戶',
    stageContacted: '已聯繫',
    stageNurturing: '培養中',
    stageHot: '高意願',
    stageViewing: '看房中',
    stageNegotiating: '議價中',
    stageWon: '已成交',
    stageLost: '已流失',
    stagePostSale: '售後',
    stageReferral: '轉介',
  },
};

const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port;
  if (!p || p === '80' || p === '443') return `${window.location.protocol}//${h}/api/v1`;
  if (p === '3000') return `http://${h}:8000/api/v1`;
  return `http://${h}:${p}/api/v1`;
})();

const req = async (url, opts = {}) => {
  const token = localStorage.getItem('spo_auth_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${url}`, { ...opts, headers: { ...headers, ...opts.headers } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
  return res.json();
};

const SOURCE_COLORS = {
  website: 'blue', facebook: 'geekblue', line: 'green', google_ads: 'orange',
  referral: 'gold', portal: 'purple', chatbot: 'cyan', manual: 'default',
};

const SOURCE_SITE_LABELS = {
  lifepropertyasia: 'LifeProperty',
  thailand_property: 'ThailandProperty',
  terrabkk: 'TerraBKK',
  homejet: 'HomeJet',
  fazwaz: 'FazWaz',
  livinginsider: 'LivingInsider',
  bangkok_office_finder: 'BangkokOffice',
  bangkok_coworking: 'CoWorking',
  manual: 'Manual',
};
const SOURCE_SITE_COLORS = {
  lifepropertyasia: 'blue', thailand_property: 'green', terrabkk: 'purple',
  homejet: 'orange', fazwaz: 'cyan', livinginsider: 'magenta',
  bangkok_office_finder: 'gold', bangkok_coworking: 'lime', manual: 'default',
};

export default function HouseDashboardPage() {
  const { i18n } = useTranslation();

  // Sync with platform language switcher
  const platformLang = i18n.language;
  const mappedLang = platformLang === 'zh-TW' ? 'zh' : platformLang === 'th' ? 'th' : 'en';

  const [lang, setLang] = useState(() => mappedLang || localStorage.getItem('house_admin_lang') || 'en');
  const t = T[lang] || T.en;

  // Auto-sync when platform language changes
  useEffect(() => {
    const newLang = i18n.language === 'zh-TW' ? 'zh' : i18n.language === 'th' ? 'th' : 'en';
    setLang(newLang);
  }, [i18n.language]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [aiActivity, setAiActivity] = useState([]);
  const [negotiations, setNegotiations] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [aftercarePlans, setAftercarePlans] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [propModal, setPropModal] = useState(false);
  const [leadModal, setLeadModal] = useState(false);
  const [leadDrawer, setLeadDrawer] = useState(null);
  const [leadTimeline, setLeadTimeline] = useState([]);
  const [leadRecs, setLeadRecs] = useState([]);
  const [viewProp, setViewProp] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [adPosts, setAdPosts] = useState([]);
  const [adPostLeads, setAdPostLeads] = useState([]);
  const [adPostDrawer, setAdPostDrawer] = useState(null);
  const [propForm] = Form.useForm();
  const [leadForm] = Form.useForm();

  const switchLang = (l) => {
    setLang(l);
    localStorage.setItem('house_admin_lang', l);
    // Also sync to platform i18n
    const platformKey = l === 'zh' ? 'zh-TW' : l === 'th' ? 'th' : 'en';
    i18n.changeLanguage(platformKey);
    localStorage.setItem('spo_language', platformKey);
  };

  const STAGE_CONFIG = {
    new: { color: '#1890ff', icon: <StarOutlined />, label: t.stageNew },
    contacted: { color: '#13c2c2', icon: <PhoneOutlined />, label: t.stageContacted },
    nurturing: { color: '#722ed1', icon: <HeartOutlined />, label: t.stageNurturing },
    hot: { color: '#fa541c', icon: <FireOutlined />, label: t.stageHot },
    viewing: { color: '#eb2f96', icon: <EyeOutlined />, label: t.stageViewing },
    negotiating: { color: '#faad14', icon: <DollarOutlined />, label: t.stageNegotiating },
    closed_won: { color: '#52c41a', icon: <TrophyOutlined />, label: t.stageWon },
    closed_lost: { color: '#8c8c8c', icon: <ClockCircleOutlined />, label: t.stageLost },
    post_sale: { color: '#2f54eb', icon: <GiftOutlined />, label: t.stagePostSale },
    referral_source: { color: '#d4a537', icon: <UserOutlined />, label: t.stageReferral },
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        req('/house/dashboard'),
        req(`/house/properties?limit=100${sourceFilter !== 'all' ? `&source_type=${sourceFilter}` : ''}`),
        req('/house/leads?limit=100'),
        req('/house/leads/pipeline'),
        req('/house/dashboard/ai-activity'),
      ]);
      const [s, p, l, pl, ai] = results.map((r) => (r.status === 'fulfilled' ? r.value : null));
      if (s) setStats(s);
      if (p) setProperties(p);
      if (l) setLeads(l);
      if (pl) setPipeline(pl);
      if (ai) setAiActivity(ai);
      results.forEach((r, i) => { if (r.status === 'rejected') console.error(`fetchAll[${i}] failed:`, r.reason); });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [sourceFilter]);

  const fetchAdPosts = useCallback(async () => {
    try {
      const data = await req('/jj/ad-posts?channel=facebook&limit=100');
      setAdPosts(data.posts || []);
    } catch (e) { console.error('fetchAdPosts failed:', e); }
  }, []);

  const viewAdPostLeads = async (id) => {
    try {
      const data = await req(`/jj/ad-posts/${id}/leads`);
      setAdPostLeads(data.leads || []);
      setAdPostDrawer(data.post || { id });
    } catch (e) { message.error(e.message); }
  };

  const fetchExtra = useCallback(async () => {
    try {
      const [n, c, ap, r, tt] = await Promise.all([
        req('/house/negotiations'),
        req('/house/contracts'),
        req('/house/aftercare/plans'),
        req('/house/referrals'),
        req('/house/tours'),
      ]);
      setNegotiations(n);
      setContracts(c);
      setAftercarePlans(ap);
      setReferrals(r);
      setTours(tt);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); fetchExtra(); fetchAdPosts(); }, [fetchAll, fetchExtra, fetchAdPosts]);

  const importDemo = async () => {
    try {
      message.loading({ content: t.generatingDemo, key: 'demo' });
      await req('/house/properties/import-demo', { method: 'POST' });
      message.success({ content: t.demoImported, key: 'demo' });
      fetchAll();
    } catch (e) { message.error(e.message); }
  };

  // HomeJet integration
  const [homejetUnread, setHomejetUnread] = React.useState(0);
  const [homejetImporting, setHomejetImporting] = React.useState(false);
  const [fazwazImporting, setFazwazImporting] = React.useState(false);
  const [lifepropertyImporting, setLifePropertyImporting] = React.useState(false);
  const [crawlerEnabled, setCrawlerEnabled] = React.useState(true);
  const [crawlerLoading, setCrawlerLoading] = React.useState(false);

  const fetchCrawlerStatus = React.useCallback(async () => {
    try {
      const r = await req('/house/crawler/enabled');
      setCrawlerEnabled(!!r.enabled);
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchCrawlerStatus(); }, [fetchCrawlerStatus]);

  const toggleCrawler = async (enabled) => {
    setCrawlerLoading(true);
    try {
      await req('/house/crawler/enabled', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      });
      setCrawlerEnabled(enabled);
      message.success(enabled ? 'External crawler enabled' : 'External crawler disabled');
    } catch (e) {
      message.error(e.message || t.crawlerError);
    } finally {
      setCrawlerLoading(false);
    }
  };

  const fetchHomejetUnread = React.useCallback(async () => {
    try {
      const r = await req('/house/homejet/unread-count');
      setHomejetUnread(r.unread || 0);
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchHomejetUnread(); }, [fetchHomejetUnread]);

  const importHomejet = async () => {
    if (!crawlerEnabled) { message.warning('External crawler is disabled'); return; }
    setHomejetImporting(true);
    try {
      message.loading({ content: 'Importing from HomeJet...', key: 'hj' });
      const r = await req('/house/homejet/import', {
        method: 'POST',
        body: JSON.stringify({ listing_type: 'rent', force: false }),
      });
      if (r.rate_limited) {
        message.info({ content: `Rate limited — next fetch in ${r.next_fetch_in_hours}h`, key: 'hj' });
      } else {
        message.success({ content: `HomeJet: +${r.added} new, ${r.updated} updated`, key: 'hj' });
        fetchAll();
        fetchHomejetUnread();
      }
    } catch (e) { message.error({ content: e.message, key: 'hj' }); }
    finally { setHomejetImporting(false); }
  };

  const markHomejetRead = async () => {
    try { await req('/house/homejet/mark-read', { method: 'POST' }); setHomejetUnread(0); }
    catch { /* ignore */ }
  };

  const importFazwaz = async () => {
    if (!crawlerEnabled) { message.warning('External crawler is disabled'); return; }
    setFazwazImporting(true);
    try {
      message.loading({ content: 'Importing from FazWaz...', key: 'fz' });
      const r = await req('/house/fazwaz/import', {
        method: 'POST',
        body: JSON.stringify({ source: 'thailand_property', listing_type: 'rent', force: false }),
      });
      if (r.rate_limited) {
        message.info({ content: `Rate limited - next fetch in ${r.next_fetch_in_hours}h`, key: 'fz' });
      } else {
        message.success({ content: `FazWaz: +${r.added} new, ${r.updated} updated`, key: 'fz' });
        fetchAll();
      }
    } catch (e) { message.error({ content: e.message, key: 'fz' }); }
    finally { setFazwazImporting(false); }
  };

  const importLifeProperty = async () => {
    if (!crawlerEnabled) { message.warning('External crawler is disabled'); return; }
    setLifePropertyImporting(true);
    try {
      message.loading({ content: 'Importing from Life Property Asia...', key: 'lp' });
      const r = await req('/house/lifeproperty/import', {
        method: 'POST',
        body: JSON.stringify({ list_type: 'Condo', category: 'Rent', max_pages: 3 }),
      });
      if (r.total_fetched !== undefined) {
        message.success({ content: `LifeProperty: +${r.added} new, ${r.updated} updated, ${r.skipped} skipped`, key: 'lp' });
        fetchAll();
      } else {
        message.error({ content: r.detail || 'Import failed', key: 'lp' });
      }
    } catch (e) { message.error({ content: e.message, key: 'lp' }); }
    finally { setLifePropertyImporting(false); }
  };


  const batchGenerateTours = async () => {
    try {
      message.loading({ content: t.generatingTours, key: 'tours' });
      const r = await req('/house/tours/batch-generate', { method: 'POST' });
      message.success({ content: `${r.created} ${t.toursGenerated}`, key: 'tours' });
      fetchExtra();
    } catch (e) { message.error(e.message); }
  };

  const createProperty = async (vals) => {
    try {
      await req('/house/properties', { method: 'POST', body: JSON.stringify(vals) });
      message.success(t.propertyCreated);
      setPropModal(false);
      propForm.resetFields();
      fetchAll();
    } catch (e) { message.error(e.message); }
  };

  const createLead = async (vals) => {
    try {
      await req('/house/leads', { method: 'POST', body: JSON.stringify(vals) });
      message.success(t.leadCreated);
      setLeadModal(false);
      leadForm.resetFields();
      fetchAll();
    } catch (e) { message.error(e.message); }
  };

  const openLeadDrawer = async (lead) => {
    setLeadDrawer(lead);
    try {
      const [tl, recs] = await Promise.all([
        req(`/house/leads/${lead.id}/timeline`),
        req(`/house/leads/${lead.id}/recommendations`),
      ]);
      setLeadTimeline(tl);
      setLeadRecs(recs);
    } catch (e) { console.error(e); }
  };

  const aiEnhance = async (id) => {
    try {
      message.loading({ content: t.aiEnhancing, key: 'ai' });
      await req(`/house/properties/${id}/ai-enhance`, { method: 'POST' });
      message.success({ content: t.aiEnhanced, key: 'ai' });
      fetchAll();
    } catch (e) { message.error({ content: e.message, key: 'ai' }); }
  };

  const promoteFb = async (record) => {
    try {
      message.loading({ content: 'Posting to Facebook 宇聯科技 ULINK...', key: 'fb' });
      const res = await req(`/house/properties/${record.id}/post-to-facebook`, { method: 'POST' });
      if (res.ok) {
        message.destroy('fb');
        Modal.success({
          title: '✅ Posted to Facebook!',
          content: (
            <div>
              <p>Property <strong>{record.title}</strong> posted to 宇聯科技 ULINK page.</p>
              {res.post_url && (
                <p>
                  <a href={res.post_url} target="_blank" rel="noreferrer">
                    🔗 View on Facebook →
                  </a>
                </p>
              )}
              <p style={{ fontSize: 12, color: '#999' }}>Post ID: {res.post_id}</p>
            </div>
          ),
        });
      } else {
        message.error({ content: res.error || 'Facebook post failed — check FB settings', key: 'fb' });
      }
    } catch (e) { message.error({ content: e.message, key: 'fb' }); }
  };

  const promoteGads = async (record) => {
    try {
      message.loading({ content: t.gadsCreating, key: 'gads' });
      const res = await req(`/house/properties/${record.id}/create-gads`, { method: 'POST' });
      if (res.ok) {
        message.destroy('gads');
        Modal.success({
          title: `✅ ${t.gadsCreated}`,
          content: (
            <div>
              <p><strong>{record.title}</strong> {t.gadsCreatedDesc}</p>
              <p style={{ fontSize: 12, color: '#999' }}>
                {t.gadsStatus}：<Tag color="orange">{res.status || 'PAUSED'}</Tag>{t.gadsReviewNote}
              </p>
              <p>Campaign: <Text code>{res.campaign_resource}</Text></p>
              {res.google_ads_url && (
                <p>
                  <a href={res.google_ads_url} target="_blank" rel="noreferrer">
                    🔗 {t.gadsManage} →
                  </a>
                </p>
              )}
            </div>
          ),
        });
      } else {
        message.error({ content: res.error || t.gadsFailed, key: 'gads' });
      }
    } catch (e) { message.error({ content: e.message, key: 'gads' }); }
  };

  const aiRespondToLead = async (leadId) => {
    try {
      message.loading({ content: t.aiResponding, key: 'air' });
      await req(`/house/leads/${leadId}/ai-respond`, { method: 'POST' });
      message.success({ content: t.aiResponseSent, key: 'air' });
      if (leadDrawer?.id === leadId) openLeadDrawer(leadDrawer);
    } catch (e) { message.error({ content: e.message, key: 'air' }); }
  };

  const fmtPrice = (p, cur = 'THB') => {
    if (!p) return '-';
    if (p >= 1000000) return `${(p / 1000000).toFixed(1)}M ${cur}`;
    if (p >= 1000) return `${(p / 1000).toFixed(0)}K ${cur}`;
    return `${p.toLocaleString()} ${cur}`;
  };

  const buildGoogleMapsUrl = (p) => {
    const parts = [p?.address, p?.district, p?.city, p?.country].filter(Boolean);
    const q = parts.join(', ');
    if (!q) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };

  const StatCard = ({ icon, title, value, suffix, color, trend }) => (
    <Card style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14,
          background: `linear-gradient(135deg, ${color}20, ${color}40)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, color,
        }}>{icon}</div>
        <div>
          <Text type="secondary" style={{ fontSize: 13 }}>{title}</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
            {value}{suffix && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 4 }}>{suffix}</span>}
          </div>
          {trend && <Text style={{ fontSize: 12, color: trend > 0 ? '#52c41a' : '#ff4d4f' }}>
            {trend > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(trend)}% {t.vsLastMonth}
          </Text>}
        </div>
      </div>
    </Card>
  );

  const propertyColumns = [
    {
      title: t.colProperty, dataIndex: 'title', key: 'title',
      render: (text, r) => (
        <div>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <a style={{ color: '#1677ff', fontWeight: 600, cursor: 'pointer' }} onClick={() => setViewProp(r)}>{text}</a>
            {r.is_new && <Tag color="red" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>NEW</Tag>}
            {r.source_type === 'homejet' && <Tag color="orange" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>HomeJet</Tag>}
          </span>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}><EnvironmentOutlined /> {r.district}, {r.city}</Text>
            <a onClick={() => setViewProp(r)} style={{ fontSize: 11, marginLeft: 8, color: '#1677ff', cursor: 'pointer' }}>檢視詳情</a>
            {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, marginLeft: 8 }}>🔗 View</a>}
          </div>
        </div>
      ),
    },
    { title: t.colType, dataIndex: 'property_type', key: 'type', render: v => <Tag color="blue">{v}</Tag> },
    { title: t.colListing, dataIndex: 'listing_type', key: 'lt', render: v => <Tag color={v === 'sale' ? 'red' : 'green'}>{v}</Tag> },
    {
      title: t.colSourceSite, dataIndex: 'source_type', key: 'source',
      width: 140,
      render: (v) => v ? <Tag color={SOURCE_SITE_COLORS[v] || 'default'}>{SOURCE_SITE_LABELS[v] || v}</Tag> : <Text type="secondary">-</Text>,
      filters: Object.keys(SOURCE_SITE_LABELS).map((k) => ({ text: SOURCE_SITE_LABELS[k], value: k })),
      onFilter: (value, r) => r.source_type === value,
    },
    { title: t.colPrice, dataIndex: 'price', key: 'price', render: (p, r) => <Text strong style={{ color: '#e8363a' }}>{fmtPrice(p, r.price_currency)}</Text>, sorter: (a, b) => a.price - b.price },
    { title: t.colBeds, dataIndex: 'bedrooms', key: 'beds' },
    { title: t.colArea, dataIndex: 'area_sqm', key: 'area', render: a => a ? `${a} sqm` : '-' },
    {
      title: t.colStats, key: 'stats',
      render: (_, r) => (
        <Space size={12}>
          <Tooltip title={t.tipViews}><EyeOutlined /> {r.view_count}</Tooltip>
          <Tooltip title={t.tipInquiries}><MessageOutlined /> {r.inquiry_count}</Tooltip>
        </Space>
      ),
    },
    { title: t.colStatus, dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'active' ? 'green' : s === 'sold' ? 'gold' : 'default'}>{s}</Tag> },
    {
      title: t.colActions, key: 'actions',
      render: (_, r) => (
        <Space>
          <Button size="small" type="primary" ghost icon={<FileTextOutlined />} onClick={() => setViewProp(r)}>詳情</Button>
          <Tooltip title={t.tipAiEnhance}><Button size="small" icon={<RobotOutlined />} onClick={() => aiEnhance(r.id)} /></Tooltip>
          <Tooltip title={t.tipFbPost}><Button size="small" icon={<FacebookOutlined />} onClick={() => promoteFb(r)} style={{ color: '#1877F2' }} /></Tooltip>
          <Tooltip title={t.tipGads}><Button size="small" icon={<GoogleOutlined />} onClick={() => promoteGads(r)} style={{ color: '#4285F4' }} /></Tooltip>
          <Tooltip title={t.tipViewPortal}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(`/h/life-property/properties/${r.id}`, '_blank')} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const leadColumns = [
    {
      title: t.colLead, dataIndex: 'name', key: 'name',
      render: (n, r) => (
        <a onClick={() => openLeadDrawer(r)}>
          <Text strong>{n}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text></div>
        </a>
      ),
    },
    { title: t.colScore, dataIndex: 'lead_score', key: 'score', render: s => <Progress type="circle" percent={s} size={40} strokeColor={s > 70 ? '#52c41a' : s > 40 ? '#faad14' : '#ff4d4f'} />, sorter: (a, b) => a.lead_score - b.lead_score },
    {
      title: t.colStage, dataIndex: 'stage', key: 'stage',
      render: s => { const c = STAGE_CONFIG[s] || { color: '#999', label: s }; return <Tag color={c.color} icon={c.icon}>{c.label}</Tag>; },
      filters: Object.entries(STAGE_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (v, r) => r.stage === v,
    },
    { title: t.colSource, dataIndex: 'source', key: 'source', render: s => <Tag color={SOURCE_COLORS[s] || 'default'}>{s}</Tag> },
    { title: t.colBudget, key: 'budget', render: (_, r) => r.budget_min || r.budget_max ? `${fmtPrice(r.budget_min)} - ${fmtPrice(r.budget_max)}` : '-' },
    { title: t.colNationality, dataIndex: 'nationality', key: 'nat' },
    {
      title: t.colActions, key: 'actions',
      render: (_, r) => (
        <Space>
          <Tooltip title={t.tipAiRespond}><Button size="small" type="primary" icon={<RobotOutlined />} onClick={() => aiRespondToLead(r.id)} /></Tooltip>
          <Tooltip title={t.tipDetails}><Button size="small" icon={<EyeOutlined />} onClick={() => openLeadDrawer(r)} /></Tooltip>
        </Space>
      ),
    },
  ];

  const PipelineView = () => (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '8px 0' }}>
      {pipeline.map(p => {
        const c = STAGE_CONFIG[p.stage] || { color: '#999', label: p.stage };
        return (
          <Card key={p.stage} size="small" style={{
            minWidth: 160, borderRadius: 12, borderTop: `3px solid ${c.color}`,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{p.count}</div>
            <div style={{ fontSize: 11, color: '#999' }}>{fmtPrice(p.total_value)}</div>
          </Card>
        );
      })}
    </div>
  );

  const AIActivityFeed = () => (
    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
      {aiActivity.length === 0 ? <Empty description={t.noAiActivity} /> :
        <Timeline>
          {aiActivity.map((a, i) => (
            <Timeline.Item key={i} color="blue" dot={<RobotOutlined style={{ fontSize: 14 }} />}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <Tag color="cyan">{a.activity_type}</Tag>
                  <Text style={{ fontSize: 13 }}>{a.summary}</Text>
                  {a.channel && <Tag style={{ marginLeft: 8 }}>{a.channel}</Tag>}
                </div>
                <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                  {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                </Text>
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      }
    </div>
  );

  const langMenuItems = [
    { key: 'en', label: 'English' },
    { key: 'th', label: 'ภาษาไทย' },
    { key: 'zh', label: '繁體中文' },
  ];

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <Title level={3} style={{ margin: 0, background: 'linear-gradient(135deg, #1a1a2e, #e8363a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            <HomeOutlined /> {t.platformTitle}
          </Title>
          <Text type="secondary">{t.platformSubtitle}</Text>
        </div>
        <Space wrap>
          <Dropdown menu={{ items: langMenuItems, onClick: ({ key }) => switchLang(key), selectedKeys: [lang] }}>
            <Button icon={<TranslationOutlined />}>
              {lang === 'en' ? 'EN' : lang === 'th' ? 'TH' : 'ZH'}
            </Button>
          </Dropdown>
          <Tooltip title={crawlerEnabled ? t.crawlerOnDesc : t.crawlerOffDesc}>
            <Space>
              <span style={{ fontSize: 13, color: crawlerEnabled ? '#52c41a' : '#999' }}>
                {crawlerEnabled ? t.crawlerOn : t.crawlerOff}
              </span>
              <Switch
                checked={crawlerEnabled}
                loading={crawlerLoading}
                onChange={toggleCrawler}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Tooltip>
          <Button icon={<ImportOutlined />} onClick={importDemo}>{t.importDemo}</Button>
          <Button icon={<VideoCameraOutlined />} onClick={batchGenerateTours}>{t.generateTours}</Button>
          <Badge count={homejetUnread} offset={[-4, 4]}>
            <Button
              icon={<span>🏠</span>} loading={homejetImporting}
              onClick={importHomejet}
              style={{ borderColor: homejetUnread > 0 ? '#ff4d4f' : undefined }}
            >
              HomeJet {homejetUnread > 0 ? `(${homejetUnread} new)` : 'Import'}
            </Button>
          </Badge>
          <Badge count={0} offset={[-4, 4]}>
            <Button
              icon={<span>🏠</span>} loading={fazwazImporting}
              onClick={importFazwaz}
              style={{ borderColor: '#fa8c16' }}
            >
              FazWaz Import
            </Button>
          </Badge>
          <Badge count={0} offset={[-4, 4]}>
            <Button
              icon={<span>🏠</span>} loading={lifepropertyImporting}
              onClick={importLifeProperty}
              style={{ borderColor: '#52c41a' }}
            >
              LifeProperty Import
            </Button>
          </Badge>
          <Button icon={<PlusOutlined />} onClick={() => setPropModal(true)}>{t.addProperty}</Button>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => setLeadModal(true)}>{t.addLead}</Button>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchAll(); fetchExtra(); }}>{t.refresh}</Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" items={[
        {
          key: 'dashboard', label: <span><DollarOutlined /> {t.tabDashboard}</span>,
          children: (
            <Spin spinning={loading}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}><StatCard icon={<HomeOutlined />} title={t.properties} value={stats?.total_properties || 0} color="#1890ff" /></Col>
                <Col xs={12} sm={6}><StatCard icon={<TeamOutlined />} title={t.totalLeads} value={stats?.total_leads || 0} color="#722ed1" /></Col>
                <Col xs={12} sm={6}><StatCard icon={<FireOutlined />} title={t.activeLeads} value={stats?.active_leads || 0} color="#fa541c" /></Col>
                <Col xs={12} sm={6}><StatCard icon={<TrophyOutlined />} title={t.dealsMonth} value={stats?.deals_this_month || 0} color="#52c41a" /></Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={12} sm={6}><StatCard icon={<DollarOutlined />} title={t.revenueMonth} value={fmtPrice(stats?.revenue_this_month)} color="#d4a537" /></Col>
                <Col xs={12} sm={6}><StatCard icon={<RobotOutlined />} title={t.aiResponsesToday} value={stats?.ai_responses_today || 0} color="#13c2c2" /></Col>
                <Col xs={12} sm={6}><StatCard icon={<ArrowUpOutlined />} title={t.conversionRate} value={`${(stats?.conversion_rate || 0).toFixed(1)}%`} color="#eb2f96" /></Col>
                <Col xs={12} sm={6}><StatCard icon={<EyeOutlined />} title={t.portalViews} value={properties.reduce((s, p) => s + (p.view_count || 0), 0)} color="#2f54eb" /></Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={14}>
                  <Card title={<><ThunderboltOutlined /> {t.leadPipeline}</>} style={{ borderRadius: 16 }}>
                    <PipelineView />
                  </Card>
                </Col>
                <Col xs={24} lg={10}>
                  <Card title={<><RobotOutlined /> {t.aiActivityFeed}</>} style={{ borderRadius: 16 }}>
                    <AIActivityFeed />
                  </Card>
                </Col>
              </Row>

              {stats?.top_properties?.length > 0 && (
                <Card title={<><StarOutlined /> {t.topProperties}</>} style={{ borderRadius: 16, marginTop: 16 }}>
                  <Row gutter={[12, 12]}>
                    {stats.top_properties.map(p => (
                      <Col xs={24} sm={12} md={8} lg={6} key={p.id}>
                        <Card size="small" style={{ borderRadius: 12 }}
                          cover={<div style={{
                            height: 120, background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}><HomeOutlined style={{ fontSize: 32, color: '#fff' }} /></div>}>
                          <Text strong style={{ fontSize: 13 }}>{p.title}</Text>
                          <div><Text style={{ color: '#e8363a', fontWeight: 600 }}>{fmtPrice(p.price)}</Text></div>
                          <div style={{ marginTop: 4 }}>
                            <Space size={4}>
                              <Tag size="small"><EyeOutlined /> {p.view_count}</Tag>
                              <Tag size="small"><MessageOutlined /> {p.inquiry_count}</Tag>
                            </Space>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </Spin>
          ),
        },
        {
          key: 'properties', label: (
            <span onClick={homejetUnread > 0 ? markHomejetRead : undefined}>
              <HomeOutlined /> {t.tabProperties} ({properties.length})
              {homejetUnread > 0 && (
                <Badge count={homejetUnread} style={{ marginLeft: 6, backgroundColor: '#ff4d4f' }} />
              )}
            </span>
          ),
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Space style={{ marginBottom: 12 }} wrap>
                <Select
                  value={sourceFilter} onChange={setSourceFilter} style={{ width: 220 }}
                  placeholder={t.colSourceSite}
                >
                  <Option value="all">{t.allSources}</Option>
                  {Object.keys(SOURCE_SITE_LABELS).map((k) => (
                    <Option key={k} value={k}>{SOURCE_SITE_LABELS[k]}</Option>
                  ))}
                </Select>
                <Text type="secondary">{properties.length} {t.tabProperties}</Text>
              </Space>
              <Table
                dataSource={properties} columns={propertyColumns} rowKey="id"
                size="middle" pagination={{ pageSize: 15 }}
                scroll={{ x: 1000 }}
              />
            </Card>
          ),
        },
        {
          key: 'adposts', label: <span><FacebookOutlined /> {t.tabAdPosts} ({adPosts.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Table
                dataSource={adPosts} rowKey="id" size="middle"
                pagination={{ pageSize: 15 }} scroll={{ x: 900 }}
                columns={[
                  { title: t.colId, dataIndex: 'id', key: 'id', width: 60 },
                  { title: t.colType, dataIndex: 'channel', key: 'channel', width: 100, render: (c) => <Tag color="geekblue">{c}</Tag> },
                  { title: t.adPostLink, dataIndex: 'url', key: 'url', width: 100, render: (u) => u ? <a href={u} target="_blank" rel="noreferrer">FB ↗</a> : <Text type="secondary">-</Text> },
                  { title: t.tabProperties, dataIndex: 'title', key: 'title', ellipsis: true, render: (x, r) => r.property_id ? `${x || ''}  (Ref#${r.property_id})` : (x || '-') },
                  { title: t.adPostPublished, dataIndex: 'published_at', key: 'published_at', width: 170, render: (d) => d ? new Date(d).toLocaleString() : '-' },
                  { title: t.adPostLeads, dataIndex: 'lead_count', key: 'lead_count', width: 90, render: (n) => <Badge count={n || 0} showZero color={n ? '#52c41a' : '#d9d9d9'} /> },
                  { title: t.colActions, key: 'actions', width: 120, render: (_, r) => <Button size="small" type="link" icon={<TeamOutlined />} onClick={() => viewAdPostLeads(r.id)}>{t.adViewLeads}</Button> },
                ]}
              />
            </Card>
          ),
        },
        {
          key: 'leads', label: <span><TeamOutlined /> {t.tabLeads} ({leads.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <div style={{ marginBottom: 16 }}><PipelineView /></div>
              <Table
                dataSource={leads} columns={leadColumns} rowKey="id"
                size="middle" pagination={{ pageSize: 15 }}
                scroll={{ x: 900 }}
              />
            </Card>
          ),
        },
        {
          key: 'negotiations', label: <span><DollarOutlined /> {t.tabNegotiations} ({negotiations.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Table dataSource={negotiations} rowKey="id" size="middle" columns={[
                { title: t.colId, dataIndex: 'id', key: 'id' },
                { title: t.colPropertyId, dataIndex: 'property_id', key: 'prop' },
                { title: t.colLeadId, dataIndex: 'lead_id', key: 'lead' },
                { title: t.colAsking, dataIndex: 'asking_price', key: 'ask', render: p => fmtPrice(p) },
                { title: t.colOffer, dataIndex: 'offer_price', key: 'offer', render: p => fmtPrice(p) },
                { title: t.colFinal, dataIndex: 'final_price', key: 'final', render: p => fmtPrice(p) },
                { title: t.colStatus, dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'agreed' ? 'green' : s === 'cancelled' ? 'red' : 'blue'}>{s}</Tag> },
              ]} />
            </Card>
          ),
        },
        {
          key: 'contracts', label: <span><FileTextOutlined /> {t.tabContracts} ({contracts.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Table dataSource={contracts} rowKey="id" size="middle" columns={[
                { title: t.colId, dataIndex: 'id', key: 'id' },
                { title: t.colContractType, dataIndex: 'contract_type', key: 'type', render: v => <Tag>{v}</Tag> },
                { title: t.colPropertyId, dataIndex: 'property_id', key: 'prop' },
                { title: t.colLeadId, dataIndex: 'lead_id', key: 'lead' },
                { title: t.colStatus, dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'completed' ? 'green' : s === 'voided' ? 'red' : 'blue'}>{s}</Tag> },
                { title: t.colCreated, dataIndex: 'created_at', key: 'created', render: d => d ? new Date(d).toLocaleDateString() : '-' },
              ]} />
            </Card>
          ),
        },
        {
          key: 'tours', label: <span><VideoCameraOutlined /> {t.tabTours} ({tours.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Table dataSource={tours} rowKey="id" size="middle" columns={[
                { title: t.colId, dataIndex: 'id', key: 'id' },
                { title: t.colPropertyId, dataIndex: 'property_id', key: 'prop' },
                { title: t.colTourType, dataIndex: 'tour_type', key: 'type', render: v => <Tag color="purple">{v}</Tag> },
                { title: t.colStatus, dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'ready' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag> },
                { title: t.colTourViews, dataIndex: 'view_count', key: 'views' },
                { title: t.colAiGenerated, dataIndex: 'ai_generated', key: 'ai', render: v => v ? <Tag color="cyan">{t.aiTag}</Tag> : <Tag>{t.manualTag}</Tag> },
                { title: t.colActions, key: 'actions', render: (_, r) => r.status === 'ready' ? (
                  <Tooltip title={t.viewTour}><Button size="small" icon={<EyeOutlined />} onClick={() => window.open(`/h/life-property/tour/${r.property_id}`, '_blank')} /></Tooltip>
                ) : null },
              ]} />
            </Card>
          ),
        },
        {
          key: 'aftercare', label: <span><HeartOutlined /> {t.tabAftercare} ({aftercarePlans.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Table dataSource={aftercarePlans} rowKey="id" size="middle" columns={[
                { title: t.colId, dataIndex: 'id', key: 'id' },
                { title: t.colPlanType, dataIndex: 'plan_type', key: 'type', render: v => <Tag color="blue">{v}</Tag> },
                { title: t.colLeadId, dataIndex: 'lead_id', key: 'lead' },
                { title: t.colPropertyId, dataIndex: 'property_id', key: 'prop' },
                { title: t.colStatus, dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'active' ? 'green' : s === 'completed' ? 'gold' : 'default'}>{s}</Tag> },
                {
                  title: t.colActions, key: 'actions',
                  render: (_, r) => r.status === 'active' ? (
                    <Button size="small" icon={<RobotOutlined />} onClick={async () => {
                      try { await req(`/house/aftercare/plans/${r.id}/execute-next`, { method: 'POST' }); message.success(t.milestoneExecuted); fetchExtra(); } catch (e) { message.error(e.message); }
                    }}>{t.executeNext}</Button>
                  ) : null,
                },
              ]} />
            </Card>
          ),
        },
        {
          key: 'referrals', label: <span><GiftOutlined /> {t.tabReferrals} ({referrals.length})</span>,
          children: (
            <Card style={{ borderRadius: 16 }}>
              <Table dataSource={referrals} rowKey="id" size="middle" columns={[
                { title: t.colReferrer, dataIndex: 'referrer_name', key: 'referrer' },
                { title: t.colReferred, dataIndex: 'referred_name', key: 'referred' },
                { title: t.colEmail, dataIndex: 'referred_email', key: 'email' },
                { title: t.colStatus, dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'closed' ? 'green' : s === 'viewing' ? 'orange' : 'blue'}>{s}</Tag> },
                { title: t.colReward, key: 'reward', render: (_, r) => r.reward_value ? `${r.reward_value} (${r.reward_paid ? t.paid : t.pending})` : '-' },
              ]} />
            </Card>
          ),
        },
      ]} />

      {/* Property Detail Drawer */}
      <Drawer
        title={<span><HomeOutlined /> 物件詳情</span>}
        width={560}
        open={!!viewProp}
        onClose={() => setViewProp(null)}
      >
        {viewProp && (
          <div>
            <Title level={4} style={{ marginTop: 0 }}>{viewProp.title}</Title>
            <Space style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              {viewProp.source_url && (
                <Button
                  type="primary"
                  icon={<GlobalOutlined />}
                  href={viewProp.source_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  查看原始網站照片
                </Button>
              )}
              {buildGoogleMapsUrl(viewProp) && (
                <Button
                  icon={<EnvironmentOutlined />}
                  href={buildGoogleMapsUrl(viewProp)}
                  target="_blank"
                  rel="noreferrer"
                >
                  開啟 Google 地圖
                </Button>
              )}
            </Space>
            <Space size={8} style={{ marginBottom: 16, flexWrap: 'wrap' }}>
              {viewProp.property_type && <Tag color="blue">{viewProp.property_type}</Tag>}
              {viewProp.listing_type && <Tag color={viewProp.listing_type === 'sale' ? 'red' : 'green'}>{viewProp.listing_type}</Tag>}
              <Tag color={viewProp.status === 'active' ? 'green' : 'default'}>{viewProp.status}</Tag>
              {viewProp.source_type && <Tag color="purple">{viewProp.source_type}</Tag>}
            </Space>

            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="價格">
                <Text strong style={{ color: '#e8363a' }}>{fmtPrice(viewProp.price, viewProp.price_currency)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="房型">
                {viewProp.bedrooms != null ? `${viewProp.bedrooms} 房` : '-'} / {viewProp.bathrooms != null ? `${viewProp.bathrooms} 衛` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="面積">{viewProp.area_sqm ? `${viewProp.area_sqm} sqm` : '-'}</Descriptions.Item>
              <Descriptions.Item label="區域">
                {buildGoogleMapsUrl(viewProp) ? (
                  <a href={buildGoogleMapsUrl(viewProp)} target="_blank" rel="noreferrer">
                    <EnvironmentOutlined style={{ color: '#1677ff' }} /> {[viewProp.district, viewProp.city, viewProp.country].filter(Boolean).join(', ') || '-'}
                  </a>
                ) : ([viewProp.district, viewProp.city, viewProp.country].filter(Boolean).join(', ') || '-')}
              </Descriptions.Item>
              <Descriptions.Item label="地址">
                {viewProp.address ? (
                  <a href={buildGoogleMapsUrl(viewProp) || '#'} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                    <EnvironmentOutlined style={{ color: '#1677ff' }} /> {viewProp.address}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="建案">{viewProp.building_name || viewProp.project_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="樓層">
                {viewProp.floor_number != null ? `${viewProp.floor_number} / ${viewProp.total_floors || '?'}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="描述">
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{viewProp.description || '-'}</Paragraph>
              </Descriptions.Item>
            </Descriptions>

            {viewProp.amenities && Array.isArray(viewProp.amenities) && viewProp.amenities.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>設施：</Text>
                <div style={{ marginTop: 8 }}>
                  {viewProp.amenities.map((a, i) => <Tag key={i} style={{ marginBottom: 4 }}>{a}</Tag>)}
                </div>
              </div>
            )}

            {viewProp.images && Array.isArray(viewProp.images) && viewProp.images.length > 0 && (
              <div>
                <Text strong>照片：</Text>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {viewProp.images.slice(0, 8).map((img, i) => (
                    <img key={i} src={img} alt="" style={{ width: 100, height: 80, objectFit: 'cover', borderRadius: 6 }} onError={e => e.target.style.display = 'none'} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Ad Post Leads Drawer */}
      <Drawer
        title={<><FacebookOutlined /> {t.tabAdPosts}: {adPostDrawer?.title || `#${adPostDrawer?.id || ''}`}</>}
        open={!!adPostDrawer} onClose={() => setAdPostDrawer(null)} width={560}
      >
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message={t.adPostLeads}
          description={adPostLeads.length ? undefined : t.adNoLeads}
        />
        <Table
          dataSource={adPostLeads} rowKey="id" size="small"
          pagination={{ pageSize: 10 }}
          columns={[
            { title: t.colId, dataIndex: 'id', key: 'id', width: 60 },
            { title: t.colLead, dataIndex: ['customer', 'name'], key: 'name' },
            { title: t.colSource, dataIndex: 'source', key: 'source', render: (s) => <Tag color={SOURCE_COLORS[s] || 'default'}>{s}</Tag> },
            { title: t.adPostPublished, dataIndex: 'created_at', key: 'created_at', width: 160, render: (d) => d ? new Date(d).toLocaleString() : '-' },
            { title: t.colStatus, dataIndex: 'status', key: 'status', width: 90 },
            { title: t.notes, dataIndex: 'original_text', key: 'text', ellipsis: true },
          ]}
        />
      </Drawer>

      {/* Property Creation Modal */}
      <Modal title={t.modalAddProperty} open={propModal} onCancel={() => setPropModal(false)} onOk={() => propForm.submit()} width={720} okText={t.create}>
        <Form form={propForm} layout="vertical" onFinish={createProperty} initialValues={{ partner_slug: 'life-property', property_type: 'condo', listing_type: 'rent', price_currency: 'THB', city: 'Bangkok', country: 'Thailand', furnishing: 'fully', status: 'active' }}>
          <Row gutter={16}>
            <Col span={16}><Form.Item name="title" label={t.labelTitle} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="partner_slug" label={t.labelPartnerSlug}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="property_type" label={t.labelType}><Select><Option value="condo">{t.typeCondo}</Option><Option value="house">{t.typeHouse}</Option><Option value="villa">{t.typeVilla}</Option><Option value="townhouse">{t.typeTownhouse}</Option><Option value="land">{t.typeLand}</Option><Option value="commercial">{t.typeCommercial}</Option></Select></Form.Item></Col>
            <Col span={8}><Form.Item name="listing_type" label={t.labelListing}><Select><Option value="rent">{t.listRent}</Option><Option value="sale">{t.listSale}</Option><Option value="sale_or_rent">{t.listBoth}</Option></Select></Form.Item></Col>
            <Col span={8}><Form.Item name="price" label={t.labelPrice} rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="bedrooms" label={t.labelBedrooms}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="bathrooms" label={t.labelBathrooms}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="area_sqm" label={t.labelArea}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="district" label={t.labelDistrict}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="building_name" label={t.labelBuilding}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="furnishing" label={t.labelFurnishing}><Select><Option value="fully">{t.furnFully}</Option><Option value="partial">{t.furnPartial}</Option><Option value="unfurnished">{t.furnUnfurnished}</Option></Select></Form.Item></Col>
          </Row>
          <Form.Item name="description" label={t.labelDescription}><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      {/* Lead Creation Modal */}
      <Modal title={t.modalAddLead} open={leadModal} onCancel={() => setLeadModal(false)} onOk={() => leadForm.submit()} okText={t.create}>
        <Form form={leadForm} layout="vertical" onFinish={createLead} initialValues={{ partner_slug: 'life-property', source: 'manual', stage: 'new', language_pref: 'en' }}>
          <Form.Item name="name" label={t.labelName} rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="email" label={t.labelEmail}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label={t.labelPhone}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="nationality" label={t.labelNationality}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="source" label={t.labelSource}><Select>{Object.keys(SOURCE_COLORS).map(s => <Option key={s} value={s}>{s}</Option>)}</Select></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="budget_min" label={t.labelBudgetMin}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="budget_max" label={t.labelBudgetMax}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="notes" label={t.labelNotes}><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Lead Detail Drawer */}
      <Drawer title={leadDrawer?.name || t.leadDetails} open={!!leadDrawer} onClose={() => setLeadDrawer(null)} width={600} extra={
        <Button type="primary" icon={<RobotOutlined />} onClick={() => aiRespondToLead(leadDrawer?.id)}>{t.aiRespond}</Button>
      }>
        {leadDrawer && (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label={t.labelEmail}>{leadDrawer.email}</Descriptions.Item>
              <Descriptions.Item label={t.labelPhone}>{leadDrawer.phone}</Descriptions.Item>
              <Descriptions.Item label={t.labelNationality}>{leadDrawer.nationality}</Descriptions.Item>
              <Descriptions.Item label={t.labelLanguage}>{leadDrawer.language_pref}</Descriptions.Item>
              <Descriptions.Item label={t.labelSource}><Tag color={SOURCE_COLORS[leadDrawer.source]}>{leadDrawer.source}</Tag></Descriptions.Item>
              <Descriptions.Item label={t.labelStage}><Tag color={STAGE_CONFIG[leadDrawer.stage]?.color}>{STAGE_CONFIG[leadDrawer.stage]?.label || leadDrawer.stage}</Tag></Descriptions.Item>
              <Descriptions.Item label={t.labelScore}><Progress type="circle" percent={leadDrawer.lead_score} size={40} /></Descriptions.Item>
              <Descriptions.Item label={t.colBudget}>{fmtPrice(leadDrawer.budget_min)} - {fmtPrice(leadDrawer.budget_max)}</Descriptions.Item>
            </Descriptions>

            {leadDrawer.notes && <Card size="small" style={{ marginTop: 16 }} title={t.notes}><Text>{leadDrawer.notes}</Text></Card>}

            <Divider>{t.aiRecommendedProperties}</Divider>
            {leadRecs.length === 0 ? <Empty description={t.noRecommendations} /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {leadRecs.slice(0, 5).map(r => (
                  <Card size="small" key={r.id} style={{ borderLeft: `3px solid ${r.match_score > 70 ? '#52c41a' : '#faad14'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong>{r.title || `Property #${r.property_id}`}</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{r.district} — {fmtPrice(r.price)}</Text></div>
                      </div>
                      <Progress type="circle" percent={r.match_score} size={36} strokeColor={r.match_score > 70 ? '#52c41a' : '#faad14'} />
                    </div>
                    {r.match_reasons && <div style={{ marginTop: 4 }}>{r.match_reasons.map((mr, i) => <Tag key={i} size="small" color="blue">{mr}</Tag>)}</div>}
                  </Card>
                ))}
              </div>
            )}

            <Divider>{t.activityTimeline}</Divider>
            {leadTimeline.length === 0 ? <Empty description={t.noActivities} /> : (
              <Timeline>
                {leadTimeline.map((a, i) => (
                  <Timeline.Item key={i} color={a.ai_generated ? 'blue' : 'gray'} dot={a.ai_generated ? <RobotOutlined /> : undefined}>
                    <Tag color={a.ai_generated ? 'cyan' : 'default'}>{a.activity_type}</Tag>
                    <Text style={{ fontSize: 13 }}>{a.summary}</Text>
                    <div><Text type="secondary" style={{ fontSize: 11 }}>{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</Text></div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}
